import {
  Box,
  Checkbox,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import {
  BusinessEntityType,
  GetBusinessEntityDetailsQuery,
  GoodDistributionInput,
  GoodInput,
} from "../../../../graphql/generated";
import InputAdornment from "@mui/material/InputAdornment";
import { ShipmentLocationInputData } from "../../../../redux/slices/Types";
import { sum } from "lodash";
import goodUnitLabel from "../../../../utils/labels/goodUnitsLabel";
import { useTranslation } from "react-i18next";
import LoadingOverlay from "../../../common/LoadingOverlay";
import useDialog from "../../../../utils/hooks/useDialog";
import { useMemo, useState } from "react";
import BestBuyModal from "../../../best-buy/BestBuyModal/BestBuyModal";
import BusinessEntitySelectContainer from "../BusinessEntitySelect";
import StorageFacilityLevelBar from "../../../forecasting/ForecastingDashboard/StorageFacilityLevelBar";
import { useConfirm } from "material-ui-confirm";
import { testIds } from "./ReceiverGoodsList.testIds";

export type ReceiverGoodsListProps = {
  shippedGoods: Array<GoodInput>;
  receivedGoods: Array<GoodDistributionInput>;
  shipmentLocation: ShipmentLocationInputData & {
    persistedReceivedGoods?: Array<GoodDistributionInput>;
  };
  receiverDetails: GetBusinessEntityDetailsQuery["businessEntityById"] | null;
  receiverDetailsLoading?: boolean;
  onChange: (receivedGoods: Array<GoodDistributionInput>) => void;
  bestBuyOptionEnabled?: boolean;
  inlineShipperSelection?: boolean;
  enableTankCapacityWarnings?: boolean;
};

export interface Columns {
  label: string;
  field: string;
  align?: "left" | "right" | "center";
  disablePadding?: boolean;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

export function computeVisibleGoods<T extends { _id?: string | null; goodProfileId?: string | null; label: string }>(
  shippedGoods: Array<T>,
  receiverDetails: {
    storageFacilities?: Array<any> | null;
    commodityIds?: Array<string | null> | null;
  } | null
): Array<T> {
  const hasTanks = (receiverDetails?.storageFacilities?.length ?? 0) > 0;
  const allowedIds = receiverDetails?.commodityIds?.filter(Boolean) as string[] | undefined;
  if (hasTanks || !allowedIds?.length) return shippedGoods;
  const allowed = new Set(allowedIds.map(String));
  return shippedGoods.filter((g) => !g.goodProfileId || allowed.has(String(g.goodProfileId)));
}

export default function ReceiverGoodsList({
  shippedGoods,
  receivedGoods,
  shipmentLocation,
  onChange,
  receiverDetails,
  receiverDetailsLoading,
  bestBuyOptionEnabled = true,
  inlineShipperSelection = false,
  enableTankCapacityWarnings = true,
}: ReceiverGoodsListProps) {
  const storageFacilities = receiverDetails?.storageFacilities;

  const visibleShippedGoods = useMemo(
    () => computeVisibleGoods(shippedGoods, receiverDetails ?? null),
    [shippedGoods, receiverDetails]
  );

  // Place validateTankCapacity and related hooks at the top of the component
  const { t } = useTranslation(["common", "orders"]);
  const { showDialog } = useDialog();
  const confirm = useConfirm();

  // Normalize helpers and precomputed maps to simplify matching logic
  const goodProfileByGoodId = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    shippedGoods.forEach((g) => {
      if (g._id)
        map[g._id] = (g.goodProfileId ?? undefined) as string | undefined;
    });
    return map;
  }, [shippedGoods]);

  const normalizedReceivedGoods = useMemo(() => {
    return receivedGoods.map((rg) => ({
      ...rg,
      goodProfileId:
        rg.goodProfileId || goodProfileByGoodId[rg.goodId] || undefined,
    }));
  }, [receivedGoods, goodProfileByGoodId]);

  const findReceivedGoodFor = (
    storageFacility: NonNullable<typeof storageFacilities>[number]
  ) => {
    // Find first compatible tank for fallback matching
    const firstCompatibleTank = storageFacilities?.find((sf) =>
      includesCommodity(sf, normalizedReceivedGoods[0]?.goodProfileId)
    );

    return (
      // Rule 1: Exact storageFacilityId match
      normalizedReceivedGoods.find(
        (rg) =>
          normalizeId(rg.storageFacilityId) ===
          normalizeId(storageFacility.identifier)
      ) ||
      // Rule 2: Commodity match with optional storageFacilityId
      normalizedReceivedGoods.find(
        (rg) =>
          !!rg.goodProfileId &&
          includesCommodity(storageFacility, rg.goodProfileId) &&
          (!rg.storageFacilityId ||
            normalizeId(rg.storageFacilityId) ===
              normalizeId(storageFacility.identifier))
      ) ||
      // Rule 3: Fallback - only apply to FIRST compatible tank
      (normalizedReceivedGoods.length === 1 &&
      !normalizedReceivedGoods[0].storageFacilityId &&
      storageFacility === firstCompatibleTank
        ? normalizedReceivedGoods[0]
        : undefined)
    );
  };

  /**
   * Validates tank capacity based on whether the tank is monitored or not
   * @param storageFacility - The storage facility to validate
   * @param requestedQuantity - The quantity the user wants to deliver
   * @returns Promise<boolean> - true if validation passes, false otherwise
   */
  const validateTankCapacity = async (
    storageFacility: any,
    requestedQuantity: number
  ): Promise<boolean> => {
    const safeFillLevel =
      storageFacility.safeFillLevel || storageFacility.capacity;
    const currentLevel = storageFacility.latestReading?.level || 0;
    const availableCapacity = safeFillLevel - currentLevel;

    // Check if tank is monitored (has latestReading)
    const isMonitored = !!storageFacility.latestReading;

    if (requestedQuantity > safeFillLevel) {
      // Always block if trying to exceed safe fill level (could overflow tank)
      showDialog({
        type: "error",
        title: t("common:error.title", "Error"),
        description: t(
          "orders:receiverGoodsList.exceedsTankCapacity",
          "This amount will exceed the capacity of the tank. Please reduce the quantity."
        ),
      });
      return false;
    }

    if (requestedQuantity > availableCapacity) {
      if (!isMonitored) {
        // For unmonitored tanks, block the action
        showDialog({
          type: "error",
          title: t("common:error.title", "Error"),
          description: t(
            "orders:receiverGoodsList.exceedsTankCapacity",
            "This amount will exceed the capacity of the tank. Please reduce the quantity."
          ),
        });
        return false;
      } else if (enableTankCapacityWarnings) {
        // For monitored tanks, show warning but allow to continue
        try {
          await confirm({
            title: t("common:warning", "Warning"),
            description: t(
              "orders:receiverGoodsList.exceedsAvailableCapacity",
              "The current delivery amount exceeds the available capacity. Please double check the amount"
            ),
            confirmationText: t("common:continue", "Continue"),
            cancellationText: t("common:cancel", "Cancel"),
            confirmationButtonProps: {
              color: "warning",
              variant: "contained",
            },
          });
          return true;
        } catch (error) {
          // User cancelled
          return false;
        }
      }
    }

    return true;
  };

  const columns: Array<Columns> = (
    [
      {
        label: t("common:storageFacility", "Tank"),
        field: "storageFacility",
        align: "left",
      },
      {
        label: t("common:goods", "Goods"),
        field: "label",
        disablePadding: true,
      },
      {
        label: t("common:quantity", "Quantity"),
        field: "quantity",
        align: inlineShipperSelection ? "left" : "right",
      },
    ] as Columns[]
  ).concat(
    inlineShipperSelection
      ? [
          {
            label: t("orders:shipper", "Shipper"),
            field: "shipper",
          },
          {
            label: t("orders:supplier", "Supplier"),
            field: "supplier",
          },
          {
            label: t("orders:pinCode", "PIN Code"),
            field: "pinCode",
          },
        ]
      : []
  );

  const normalizeId = (v?: string | number | null) =>
    v === undefined || v === null ? "" : String(v);

  const includesCommodity = (
    storageFacility: any,
    goodProfileId?: string | number | null
  ) => {
    if (!goodProfileId || !storageFacility?.commodityIds) return false;
    // Defensive: handle legacy numeric/string mismatches
    const ids = storageFacility.commodityIds.map((id: any) => String(id));
    return ids.includes(String(goodProfileId));
  };

  const toggleGood = (
    goodId: string,
    toggled: boolean,
    storageFacilityId?: string
  ) => {
    const shippedGood = shippedGoods.find((good) => good._id === goodId);
    if (!shippedGood) {
      return;
    }

    if (toggled) {
      const shippedQuantity = shippedGood.quantity;
      const alreadyDeliveredQuantity = sum(
        receivedGoods
          .filter((rg) => rg.goodId === goodId)
          .map((receivedGood) => receivedGood.quantity || shippedGood.quantity)
      );
      const myReceivedGood = shipmentLocation.persistedReceivedGoods?.find(
        (receivedGood) => receivedGood.goodId === goodId
      );
      const myReceivedQty = myReceivedGood
        ? myReceivedGood.quantity || shippedGood.quantity
        : 0;
      const storageFacility = storageFacilities?.find((sf) =>
        includesCommodity(sf, shippedGood.goodProfileId)
      );

      onChange(
        receivedGoods.concat({
          goodId,
          quantity:
            shippedQuantity === Infinity
              ? null
              : Math.max(
                  shippedQuantity - alreadyDeliveredQuantity + myReceivedQty,
                  0
                ),
          goodProfileId: shippedGood.goodProfileId,
          shipperId: receiverDetails?.defaultShipperId || null,
          supplierId: storageFacility?.defaultSupplierId || null,
          storageFacilityId: storageFacilityId || null,
        })
      );
    } else {
      onChange(
        receivedGoods.filter((receivedGood) =>
          storageFacilityId
            ? receivedGood.storageFacilityId !== storageFacilityId
            : receivedGood.goodId !== goodId
        )
      );
    }
  };

  const setReceivedGoodQuantity = (
    goodId: string,
    quantity: number | null,
    storageFacilityId?: string
  ) => {
    const shippedGood = shippedGoods.find((good) => good._id === goodId);
    if (!shippedGood) {
      return;
    }
    onChange(
      receivedGoods.map((receivedGood) => {
        if (
          receivedGood.goodId === goodId &&
          (!storageFacilityId ||
            receivedGood.storageFacilityId === storageFacilityId)
        ) {
          return {
            ...receivedGood,
            quantity:
              quantity === null
                ? null
                : Math.max(Math.min(quantity, shippedGood.quantity), 1),
          };
        }
        return receivedGood;
      })
    );
  };

  const onReceivedGoodChange = (
    goodId: string,
    receivedGood: GoodDistributionInput,
    storageFacilityId?: string
  ) => {
    onChange(
      receivedGoods.map((rg) =>
        rg.goodId === goodId &&
        (!storageFacilityId || rg.storageFacilityId === storageFacilityId)
          ? receivedGood
          : rg
      )
    );
  };

  if (receiverDetailsLoading) {
    return <LoadingOverlay loading={receiverDetailsLoading} />;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer>
        <Table sx={{ minWidth: 750 }} aria-labelledby="goodList">
          <TableHead>
            <TableRow>
              <StyledTableCell padding="checkbox"></StyledTableCell>
              {columns.map((col, index) => (
                <StyledTableCell
                  key={index}
                  padding={col.disablePadding ? "none" : "normal"}
                  align={col.align}
                >
                  {col.label}
                </StyledTableCell>
              ))}
              {bestBuyOptionEnabled ? (
                <StyledTableCell
                  key={columns.length - 1}
                  align="right"
                  padding="normal"
                >
                  Actions
                </StyledTableCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {storageFacilities?.length
              ? storageFacilities.map((storageFacility) => {
                  const primaryGood = shippedGoods.find(
                    (good) =>
                      good.goodProfileId &&
                      normalizeId(storageFacility.commodityIds[0]) ===
                        normalizeId(good.goodProfileId)
                  );
                  if (!primaryGood) {
                    return null;
                  }

                  // Find receivedGood for this storage facility using normalized rules
                  const receivedGood = findReceivedGoodFor(storageFacility);

                  const shippedGood =
                    shippedGoods.find(
                      (good) =>
                        good.goodProfileId &&
                        receivedGood?.goodProfileId === good.goodProfileId
                    ) || primaryGood;
                  const shippedGoodIndex = shippedGoods.indexOf(shippedGood);
                  const hasStorageFacility =
                    !storageFacilities?.length ||
                    storageFacilities.some((sf) =>
                      includesCommodity(sf, primaryGood.goodProfileId)
                    );
                  if (primaryGood.goodProfileId && !hasStorageFacility) {
                    return null;
                  }
                  const alternativeGoods = shippedGoods.filter(
                    (good) =>
                      good.goodProfileId &&
                      includesCommodity(storageFacility, good.goodProfileId)
                  );
                  return (
                    <ReceiverGoodsItem
                      key={`${storageFacility.identifier}-${primaryGood._id}`}
                      receivedGood={receivedGood}
                      goodIndex={shippedGoodIndex}
                      toggleGood={toggleGood}
                      good={shippedGood}
                      alternativeGoods={alternativeGoods}
                      onGoodQuantityChange={setReceivedGoodQuantity}
                      onChange={onReceivedGoodChange}
                      shipmentLocation={shipmentLocation}
                      bestBuyOptionEnabled={bestBuyOptionEnabled}
                      inlineShipperSelection={inlineShipperSelection}
                      customerId={receiverDetails?.parentBusinessEntityId}
                      storageFacility={storageFacility}
                      validateTankCapacity={validateTankCapacity}
                    />
                  );
                })
              : null}
            {visibleShippedGoods
              .filter((good) => {
                if (!storageFacilities?.length) return true;
                if (!good.goodProfileId) return true;
                return false;
              })
              .map((good, goodIndex) => {
                const receivedGood = receivedGoods.find(
                  (receivedGood) => receivedGood.goodId === good._id
                );
                return (
                  <ReceiverGoodsItem
                    key={good._id}
                    receivedGood={receivedGood}
                    goodIndex={visibleShippedGoods.indexOf(good)}
                    toggleGood={toggleGood}
                    good={good}
                    onGoodQuantityChange={setReceivedGoodQuantity}
                    onChange={onReceivedGoodChange}
                    shipmentLocation={shipmentLocation}
                    bestBuyOptionEnabled={bestBuyOptionEnabled}
                    inlineShipperSelection={inlineShipperSelection}
                    customerId={receiverDetails?.parentBusinessEntityId}
                    validateTankCapacity={validateTankCapacity}
                  />
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

type ReceiverGoodsItemProps = {
  receivedGood: GoodDistributionInput | undefined;
  goodIndex: number;
  toggleGood: (
    goodId: string,
    toggled: boolean,
    storageFacilityId?: string
  ) => void;
  good: GoodInput;
  alternativeGoods?: Array<GoodInput>;
  onGoodQuantityChange: (
    goodId: string,
    quantity: number | null,
    storageFacilityId?: string
  ) => void;
  onChange: (
    goodId: string,
    receivedGood: GoodDistributionInput,
    storageFacilityId?: string
  ) => void;
  shipmentLocation: ShipmentLocationInputData;
  bestBuyOptionEnabled?: boolean;
  inlineShipperSelection?: boolean;
  customerId: string;
  storageFacility?: NonNullable<
    GetBusinessEntityDetailsQuery["businessEntityById"]["storageFacilities"]
  >[0];
  validateTankCapacity: (
    storageFacility: any,
    requestedQuantity: number
  ) => Promise<boolean>;
};

const ReceiverGoodsItem = ({
  receivedGood,
  goodIndex,
  toggleGood,
  good,
  alternativeGoods,
  onGoodQuantityChange: setReceivedGoodQuantity,
  onChange,
  shipmentLocation,
  bestBuyOptionEnabled,
  inlineShipperSelection,
  customerId,
  storageFacility,
  validateTankCapacity,
}: ReceiverGoodsItemProps) => {
  const [isBestBuyOpen, setIsBestBuyOpen] = useState(false);
  const [selectedContractIds, setSelectedContractIds] = useState<
    string[] | undefined
  >(undefined);
  const { showDialog } = useDialog();
  const { t } = useTranslation(["common", "orders"]);

  // Stable per-row id for test selectors. In the storage-facility branch several
  // tanks can hold the same commodity (same `good._id`), so the row's real
  // identity is the facility; fall back to `good._id` in the plain branch.
  const rowTestId = storageFacility?.identifier ?? good._id;

  const purchase = useMemo(
    () => ({
      customerId,
      date: shipmentLocation.timeWindows[0]?.fromDate || "",
      dropoffLocation: shipmentLocation.location,
      goodProfileId: good.goodProfileId || "",
      quantity: receivedGood?.quantity || 0,
    }),
    [customerId, shipmentLocation, good, receivedGood]
  );

  return (
    <>
      <TableRow role="checkbox" tabIndex={-1} selected={!!receivedGood}>
        <TableCell padding="checkbox">
          <Checkbox
            data-testid={`${testIds.receiverGoodsListGoodCheckbox}-${rowTestId}`}
            color="primary"
            checked={!!receivedGood}
            onChange={(event, checked) =>
              toggleGood(good._id, checked, storageFacility?.identifier)
            }
            inputProps={{
              "aria-label": `${good.label} checkbox`,
            } as React.InputHTMLAttributes<HTMLInputElement>}
          />
        </TableCell>
        <TableCell>
          {storageFacility ? (
            <>
              <Tooltip
                title={
                  storageFacility.linkedStorageFacilityIds?.length
                    ? `Manifold of ${storageFacility.linkedStorageFacilityIds.join(
                        ", "
                      )}`
                    : ""
                }
              >
                <Typography>{storageFacility.identifier}</Typography>
              </Tooltip>
              <StorageFacilityLevelBar
                storageFacility={storageFacility}
              />
            </>
          ) : (
            <Typography>N/A</Typography>
          )}
        </TableCell>
        <TableCell scope="row" padding="none">
          {alternativeGoods && alternativeGoods.length > 1 ? (
            <Select data-testid={`${testIds.receiverGoodsListCommodityQuantitySelect}-${rowTestId}`}
              disabled={!receivedGood}
              value={receivedGood?.goodId || good._id}
              onChange={(event) => {
                const selectedGoodId = event.target.value as string;
                const selectedGood = alternativeGoods.find(
                  (good) => good._id === selectedGoodId
                );
                if (!selectedGood) {
                  return;
                }
                onChange(
                  receivedGood?.goodId || good._id,
                  {
                    ...receivedGood,
                    goodId: selectedGoodId,
                    goodProfileId: selectedGood.goodProfileId,
                  },
                  storageFacility?.identifier
                );
              }}
            >
              {alternativeGoods.map((alternativeGood) => (
                <MenuItem
                  data-testid={`${testIds.receiverGoodsListCommodityQuantityMenuItem}-${rowTestId}-${alternativeGood._id}`}
                  key={alternativeGood._id}
                  value={alternativeGood._id}
                >
                  {alternativeGood.label}
                </MenuItem>
              ))}
            </Select>
          ) : (
            good.label
          )}
        </TableCell>
        <TableCell
          align="right"
          sx={{ width: inlineShipperSelection ? "15%" : "50%" }}
        >
          <TextField
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography>{goodUnitLabel(good.unit)}</Typography>
                </InputAdornment>
              ),
            }}
            size="small"
            disabled={!receivedGood}
            value={receivedGood?.quantity || ""}
            placeholder={
              good.quantity === Infinity ? undefined : String(good.quantity)
            }
            inputProps={{ "data-testid": `${testIds.receiverGoodsListCommodityQuantityInput}-${rowTestId}`,
              "aria-label": `${good.label} quantity`,
              style: {
                textAlign: "right",
              },
            }}
            onChange={async (event) => {
              const requestedQuantity = parseInt(event.target.value) || 0;

              if (storageFacility && requestedQuantity > 0) {
                const isValid = await validateTankCapacity(
                  storageFacility,
                  requestedQuantity
                );
                if (!isValid) {
                  return; // Don't update the quantity if validation fails
                }
              }

              setReceivedGoodQuantity(
                good._id,
                requestedQuantity || null,
                storageFacility?.identifier
              );
            }}
            // helperText={kgToLocalWeight(good.weight || 0).toFixed(2)}
            name="commodity-quantity"
            id={`commodity-quantity-${goodIndex}`}
          />
        </TableCell>
        {inlineShipperSelection ? (
          <>
            <TableCell sx={{ width: "20%" }}>
              <BusinessEntitySelectContainer data-testid={`${testIds.receiverGoodsListShipperAutocomplete}-${rowTestId}`}
                businessEntityType={BusinessEntityType.Shipper}
                allowAddress={false}
                testId={`${testIds.receiverGoodsListShipperAutocomplete}-${rowTestId}`}
                value={receivedGood?.shipperId || null}
                onChange={(shipperId) => {
                  if (!receivedGood) {
                    return;
                  }
                  onChange(
                    receivedGood.goodId,
                    {
                      ...receivedGood,
                      shipperId,
                    },
                    storageFacility?.identifier
                  );
                }}
              />
            </TableCell>
            <TableCell sx={{ width: "20%" }}>
              <BusinessEntitySelectContainer data-testid={`${testIds.receiverGoodsListSupplierAutocomplete}-${rowTestId}`}
                businessEntityType={BusinessEntityType.Supplier}
                allowAddress={false}
                testId={`${testIds.receiverGoodsListSupplierAutocomplete}-${rowTestId}`}
                value={receivedGood?.supplierId || null}
                inputProps={{
                  required: false,
                }}
                onChange={(supplierId) => {
                  if (!receivedGood) {
                    return;
                  }
                  onChange(
                    receivedGood.goodId,
                    {
                      ...receivedGood,
                      supplierId,
                    },
                    storageFacility?.identifier
                  );
                }}
              />
            </TableCell>
            <TableCell sx={{ width: "15%" }}>
              <TextField
                fullWidth
                label={t("orders:pinCode", "Pin Code")}
                helperText={
                  receivedGood?.alternativePinCodes?.length
                    ? receivedGood.alternativePinCodes.join(", ")
                    : ""
                }
                FormHelperTextProps={{
                  sx: { whiteSpace: "nowrap" },
                }}
                name="pinCode"
                value={receivedGood?.pinCode || ""}
                size="small"
                onChange={(event) => {
                  if (!receivedGood) {
                    return;
                  }
                  onChange(
                    receivedGood.goodId,
                    {
                      ...receivedGood,
                      pinCode: event.target.value,
                    },
                    storageFacility?.identifier
                  );
                }}
              inputProps={{ "data-testid": `${testIds.receiverGoodsListPinCodeInput}-${rowTestId}` } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </TableCell>
          </>
        ) : null}
        {bestBuyOptionEnabled ? (
          <TableCell>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              {!!receivedGood ? (
                <Button data-testid={`${testIds.receiverGoodsListTrueSourceBtn}-${rowTestId}`}
                  variant="contained"
                  color="accent"
                  onClick={() => {
                    if (
                      shipmentLocation.receiver &&
                      good.goodProfileId &&
                      shipmentLocation.timeWindows[0]?.fromDate
                    ) {
                      setIsBestBuyOpen(true);
                    } else {
                      showDialog({
                        type: "primary",
                        title: t("common:error.title", "Error"),
                        description: t(
                          "orders:bestbuy.requirementError",
                          "Please, make sure to select a delivery date"
                        ),
                      });
                    }
                  }}
                >
                  {t("orders:bestbuy.title", "TrueSource")}
                </Button>
              ) : null}
            </Box>
          </TableCell>
        ) : null}
      </TableRow>
      {isBestBuyOpen &&
      receivedGood?.goodProfileId &&
      shipmentLocation.timeWindows[0]?.fromDate ? (
        <BestBuyModal
          open={isBestBuyOpen}
          purchase={purchase}
          selectedContractIds={selectedContractIds}
          selectedContractMatch={{
            supplierId: receivedGood.supplierId,
            shipperId: receivedGood.shipperId,
            unitPrice: receivedGood.unitPrice,
          }}
          pinCodeOrder={
            receivedGood.pinCode
              ? [
                  receivedGood.pinCode,
                  ...(receivedGood.alternativePinCodes || []).filter(Boolean),
                ]
              : undefined
          }
          onClose={() => setIsBestBuyOpen(false)}
          onBestBuyItemsSubmit={(bestBuyItems) => {
            setIsBestBuyOpen(false);
            setSelectedContractIds(
              bestBuyItems.map((item) => item.supplierContract._id)
            );
            const bestBuyItem = bestBuyItems[0];
            if (
              bestBuyItem &&
              receivedGood?.goodProfileId &&
              bestBuyItem.supplierContract.shipper
            ) {
              onChange(
                receivedGood.goodId,
                {
                  ...receivedGood,
                  shipperId: bestBuyItem.supplierContract.shipperId,
                  supplierId: bestBuyItem.supplierContract.supplierId,
                  unitPrice: bestBuyItem.unitPrice,
                  pinCode: bestBuyItem.supplierContract.pinCode,
                  alternativePinCodes: bestBuyItems
                    .slice(1)
                    .map((item) => item.supplierContract.pinCode || "")
                    .filter(Boolean),
                },
                storageFacility?.identifier
              );
            }
          }}
        />
      ) : null}
    </>
  );
};
